// frontend/src/App.js
import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomThemeProvider } from './contexts/ThemeModeContext'; // Corrected import
import { ProjectModalProvider } from './contexts/ProjectModalContext'; // Import ProjectModalProvider
import { TaskProvider } from './contexts/TaskContext'; // Import TaskProvider
import { UsersPermissionsProvider } from './contexts/UsersPermissionsContext'; // Import UsersPermissionsProvider
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { NotificationProvider } from './contexts/NotificationContext';
import { CREATIVE_DECISIONS_UI_ENABLED } from './config/creativeDecisionsFeature';

// Component Imports
import SignUp from './components/Auth/SignUp';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import CliAuthPage from './components/Auth/CliAuthPage';
import CliAuthLoginPage from './components/Auth/CliAuthLoginPage';
import Layout from './components/Layout/Layout';
import ProjectViewPage from './components/ProjectView/ProjectViewPage';
import AudiencesPage from './components/AudienceView/AudiencesPage';
// import TaskManagerPage from './components/TaskManager/TaskManagerPage'; // Import TaskManagerPage
import PrivateRoute from './components/Auth/PrivateRoute';
import SettingsPage from './components/Settings/SettingsPage';
import UsersPage from './components/Settings/UsersPage';
import StrategiesLibraryPage from './components/ProjectView/StrategiesLibraryPage';
import GuidesPage from './components/Guides/GuidesPage';
import InviteHandler from './components/Auth/InviteHandler';
import AllProjects from './components/AllProjects/AllProjects';
import { OnboardingsPageV2 } from '@modules/onboardings/pages/OnboardingsPageV2';
import { OnboardingEditorPageV2 } from '@modules/onboardings/pages/OnboardingEditorPageV2';
import { ArticlesPage } from '@modules/support-flow/pages/ArticlesPage';
import { ArticleEditorPage } from '@modules/support-flow/pages/ArticleEditorPage';
import { ArticleSitePage } from '@modules/support-flow/pages/ArticleSitePage';
import { WebPlacementsPage, WebABTestsPage } from '@modules/webplacements';
import { LaunchReadinessPage } from '@modules/launch-readiness';
import { LaunchActivationPage } from '@modules/launch-activation';
import { AutonomyPage } from '@modules/autonomy-facade';
import { ThemesV2Page } from '@modules/themes/pages/ThemesV2Page';
import { ThemeV2EditorPage } from '@modules/themes/pages/ThemeV2EditorPage';
import { StripeIntegrationPage } from '@modules/stripe-connect';
import { StripeOAuthReturnHandler } from '@modules/stripe-connect/components/StripeOAuthReturnHandler';
import { PaywallProductsPage } from '@modules/paywall-products';
import {
  BillingCatalogAdminPage,
  BillingInterruptionProvider,
  ProjectBillingPage,
  ProjectPaywall,
  useProjectCapability,
} from '@modules/billing-access';
import { AnalyticsSettingsPage } from '@modules/analytics-settings';
import { CreativeDecisionsPage, FunnelReportsPage, FunnelTrafficSourcesPage, ExperimentsHubPage, FUNNEL_INSIGHTS_ACCESS_CAPABILITY } from '@modules/funnel-insights';
import { StrategyCanvasPage } from '@modules/strategy-blocks/pages/StrategyCanvasPage';
import { BlockCanvasPage } from '@modules/strategy-blocks/pages/BlockCanvasPage';
import { AdminPromptSectionsPage } from '@modules/strategy-blocks/pages/AdminPromptSectionsPage';
import { IntegrationsPage } from '@modules/integrations';
import {
  ContentPlanPlatformRoutePage,
  ContentPlanRoutePage,
  TopicDetailRoutePage,
  StrategyDraftReviewPage,
} from '@modules/content-plan';
import {
  AdCreativeLabPage,
  AdminAdCreativePlatformsPage,
  CreativeDecisionRefreshRoutePage,
} from '@modules/ad-creative';
import UsersPermissionsPage from './components/Permissions/UsersPermissionsPage';
import UsagePage from './components/Admin/UsagePage';
import ProjectUsageDetails from './components/Admin/ProjectUsageDetails';
import CronTasksAdminPage from './components/Admin/CronTasksAdminPage';
import AdminSystemTemplatesPage from './components/Admin/AdminSystemTemplatesPage';
import AdminSystemPlatformsPage from './components/Admin/AdminSystemPlatformsPage';
import { ProviderCredentialsAdminPage } from '@modules/provider-credentials';
import { FooterProvider } from './contexts/FooterContext';
import ConsoleLogger from './components/Common/ConsoleLogger'; // Console logger
import GuideServiceInitializer from './components/Common/GuideServiceInitializer'; // Guide service integration
import { IntercomProvider } from 'react-use-intercom';
import IntercomBooter from './components/Common/IntercomBooter';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID || '';

function ProjectCapabilityRoute({ capability, children, adminBypass = true }) {
  const { projectId } = useParams();
  const access = useProjectCapability(projectId, capability, { adminBypass });

  if (!projectId) return <Navigate to="/all-projects" replace />;
  if (access.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  // Render an in-place hard paywall (inside <Layout>) instead of bouncing the
  // user to /all-projects. The paywall offers a self-serve Subscribe CTA.
  if (!access.allowed) return <ProjectPaywall projectId={projectId} capability={capability} />;
  return children;
}

function CreativeDecisionsFeatureRoute() {
  const { projectId } = useParams();

  if (!projectId) return <Navigate to="/all-projects" replace />;
  if (!CREATIVE_DECISIONS_UI_ENABLED) {
    return <Navigate to={`/project/${encodeURIComponent(projectId)}/reports`} replace />;
  }

  return (
    <ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY}>
      <CreativeDecisionsPage />
    </ProjectCapabilityRoute>
  );
}

function AdCreativeCapabilityRoute() {
  const location = useLocation();
  const refreshRequested = new URLSearchParams(location.search).get('mode') === 'refresh-experiment-draft';
  return refreshRequested ? (
    <ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY} adminBypass={false}>
      <CreativeDecisionRefreshRoutePage />
    </ProjectCapabilityRoute>
  ) : (
    <ProjectCapabilityRoute capability="content_plan.access">
      <AdCreativeLabPage />
    </ProjectCapabilityRoute>
  );
}

function LegacyIosPublishingRedirect() {
  const { projectId } = useParams();
  return <Navigate to={projectId ? `/project/${projectId}/web-placements` : '/all-projects'} replace />;
}

function LegacyOnboardingsRedirect() {
  const { projectId } = useParams();
  return <Navigate to={projectId ? `/project/${projectId}/onboardings-v2` : '/all-projects'} replace />;
}

function LegacyOnboardingEditorRedirect() {
  const { projectId } = useParams();
  return <Navigate to={projectId ? `/project/${projectId}/onboardings-v2` : '/all-projects'} replace />;
}

function LegacyThemesRedirect() {
  return <Navigate to="/themes-v2" replace />;
}

function LegacyThemeEditorRedirect() {
  const { themeId } = useParams();
  return <Navigate to={themeId ? `/themes-v2/${themeId}` : '/themes-v2'} replace />;
}

// New Navigation Module Providers
import { NavigationProvider, ProjectProvider } from '@navigation/contexts';
import { ProjectDefaultRedirect } from '@navigation/components';

// Test Harness Components (development only)
import AutoLogin from './components/Test/AutoLogin';
import TestAudienceSelector from './components/Test/TestAudienceSelector';
import { TestCompositeDataPicker } from './components/Test/TestCompositeDataPicker';
import ReactFlowTestPage from './components/Test/ReactFlowTestPage';
import SimpleThemeTest from './components/Test/SimpleThemeTest';
import SimpleThemeTestV2 from './components/TestComponents/SimpleThemeTestV2';
import ProductTestPage from './components/Test/ProductTestPage';
import PlacementsSandboxPage from './components/Test/PlacementsSandboxPage';

function AppContent() {
  const { currentUser, isAdmin } = useAuth();
  const location = useLocation();

  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/forgot-password';
  const isCliAuthPage = location.pathname === '/cli-auth' || location.pathname === '/cli-auth-login';
  const isInvitationPage = location.pathname === '/invite';
  const isTestPage = location.pathname.startsWith('/test-dialogs') || location.pathname.startsWith('/demo') || location.pathname === '/playbook-test';
  const isReactFlowTestPage = location.pathname === '/reactflow';
  const isTestHarnessPage = location.pathname === '/test-strategies';
  const isAudienceSelectorTestPage = location.pathname === '/test-audience-selector';
  const isCompositeDataPickerTestPage = location.pathname === '/test-data-pickers';
  const isSimpleThemeTestPage = location.pathname === '/simple-theme-test';
  const isSimpleThemeTestV2Page = location.pathname === '/simple-theme-test-v2';
  const isLegacyV1OnboardingHarnessPage = ['/test-onboarding-service', '/onboarding-editor', '/onboarding-sandbox']
    .includes(location.pathname);
  const isProductTestPage = location.pathname === '/product-test';
  const isPlacementsSandboxPage = location.pathname === '/placements-sandbox';

  // Production onboarding editor routes (full-screen without sidebar)
  const isOnboardingEditorRoute = /^\/project\/[^/]+\/onboarding\/[^/]+$/.test(location.pathname);
  const isOnboardingV2EditorRoute = /^\/project\/[^/]+\/onboarding-v2\/[^/]+$/.test(location.pathname);
  const isArticleEditorRoute = /^\/project\/[^/]+\/article\/[^/]+\/edit$/.test(location.pathname);
  const isStrategyCanvasRoute = /^\/project\/[^/]+\/strategy\/[^/]+$/.test(location.pathname);
  const isBlockCanvasRoute = /^\/project\/[^/]+\/strategy\/[^/]+\/block\/[^/]+$/.test(location.pathname);

  // ReactFlow Test Route (development only) - Playwright E2E testing
  if (isReactFlowTestPage) {
    // Only allow in development environment
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    return (
      <AutoLogin>
        <ReactFlowTestPage />
      </AutoLogin>
    );
  }

  // Simple Theme Test V2 Route (development only) - Testing if file location matters
  if (isSimpleThemeTestV2Page) {
    // Only allow in development environment
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    // SAME code as SimpleThemeTest but file is in frontend/src/ (not src/modules/)
    return <SimpleThemeTestV2 />;
  }

  // Simple Theme Test Route (development only)
  if (isSimpleThemeTestPage) {
    // Only allow in development environment
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    // Use global CustomThemeProvider (no nesting)
    return <SimpleThemeTest />;
  }

  // Test Harness Route (development only)
  if (isTestHarnessPage) {
    // Only allow in development environment
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    return <Navigate to="/reactflow" replace />;
  }

  // Audience Selector Test Route (development only)
  if (isAudienceSelectorTestPage) {
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    return (
      <AutoLogin>
        <TestAudienceSelector />
      </AutoLogin>
    );
  }

  // Composite Data Picker Test Route (development only)
  if (isCompositeDataPickerTestPage) {
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    return (
      <AutoLogin>
        <TestCompositeDataPicker />
      </AutoLogin>
    );
  }



  // Retired V1 onboarding dev harnesses. Keep them terminal while V2-only cleanup
  // proceeds so old bookmarks do not reopen V1 editors.
  if (isLegacyV1OnboardingHarnessPage) {
    return <Navigate to={currentUser ? '/all-projects' : '/login'} replace />;
  }

  // Product Test Route (development only) - Sprint 6 test page for ProductDescriptionSection/TasksTable
  // Note: ProductTestPage has built-in AutoLogin wrapper and production guard
  if (isProductTestPage) {
    return <ProductTestPage />;
  }

  // Placements Sandbox Route (development only) - PlacementService & ABTestService manual testing
  if (isPlacementsSandboxPage) {
    if (process.env.NODE_ENV === 'production') {
      return <Navigate to="/" replace />;
    }

    return (
      <AutoLogin>
        <PlacementsSandboxPage />
      </AutoLogin>
    );
  }

  // Production Onboarding V2 Editor Route (full-screen, no Layout sidebars)
  if (isOnboardingV2EditorRoute) {
    return (
      <CustomThemeProvider>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path="/project/:projectId/onboarding-v2/:onboardingId"
              element={<OnboardingEditorPageV2 />}
            />
          </Route>
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  // Production Article Editor Route (full-screen, no Layout sidebars)
  if (isArticleEditorRoute) {
    return (
      <CustomThemeProvider>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path="/project/:projectId/article/:articleId/edit"
              element={<ProjectCapabilityRoute capability="articles.access"><ArticleEditorPage /></ProjectCapabilityRoute>}
            />
          </Route>
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  // Block Screens Canvas Route (full-screen, no Layout sidebars)
  // Must be checked BEFORE isStrategyCanvasRoute since the URL is longer
  if (isBlockCanvasRoute) {
    return (
      <CustomThemeProvider>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path="/project/:projectId/strategy/:strategyId/block/:blockId"
              element={<BlockCanvasPage />}
            />
          </Route>
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  // Strategy Canvas Route (full-screen, no Layout sidebars)
  if (isStrategyCanvasRoute) {
    return (
      <CustomThemeProvider>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path="/project/:projectId/strategy/:strategyId"
              element={<StrategyCanvasPage />}
            />
          </Route>
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  // Legacy production V1 onboarding editor route. Frozen as a compatibility
  // redirect to the V2 list while V1 data conversion remains explicit.
  if (isOnboardingEditorRoute) {
    return (
      <CustomThemeProvider>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path="/project/:projectId/onboarding/:onboardingId"
              element={<LegacyOnboardingEditorRedirect />}
            />
          </Route>
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  if (isAuthPage || isCliAuthPage || isInvitationPage || isTestPage) {
    return (
      <CustomThemeProvider>
        <Routes>
          {/* TEMPORARY: Signup disabled — redirect to login */}
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/cli-auth" element={<CliAuthPage />} />
          <Route path="/cli-auth-login" element={<CliAuthLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/invite" element={<InviteHandler />} />
          <Route path="*" element={<Navigate to={currentUser ? "/all-projects" : "/login"} replace />} />
        </Routes>
      </CustomThemeProvider>
    );
  }

  return (
    <Layout> {/* Envolve as rotas protegidas/principais com o Layout */}
      {/*
        M7: global handler that detects post-OAuth landing at
        /project/:pid/integrations?stripe=connected&mode=X. When a return URL
        was stashed in sessionStorage (by the paywall product picker before
        redirecting to Stripe), this navigates back to the editor URL and
        surfaces a confirmation toast. No-ops otherwise so the existing
        Integrations page flow is unaffected.
      */}
      <StripeOAuthReturnHandler />
      <Routes>
        <Route element={<PrivateRoute />}>
          <Route path="/all-projects" element={<AllProjects />} />
          <Route
            path="/project/:projectId"
            element={
              <ProjectDefaultRedirect>
                <ProjectViewPage />
              </ProjectDefaultRedirect>
            }
          />
          <Route path="/project/:projectId/audiences" element={<ProjectCapabilityRoute capability="audiences.access"><AudiencesPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/onboardings" element={<LegacyOnboardingsRedirect />} />
          <Route path="/project/:projectId/onboardings-v2" element={<ProjectCapabilityRoute capability="web_onboarding.access"><OnboardingsPageV2 /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/articles" element={<ProjectCapabilityRoute capability="articles.access"><ArticlesPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/articles/site" element={<ProjectCapabilityRoute capability="articles.access"><ArticleSitePage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/ad-creative" element={<AdCreativeCapabilityRoute />} />
          <Route path="/project/:projectId/content-plan/creatives" element={<ProjectCapabilityRoute capability="content_plan.access"><AdCreativeLabPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/placements" element={<LegacyIosPublishingRedirect />} />
          <Route path="/project/:projectId/ab-tests" element={<LegacyIosPublishingRedirect />} />
          <Route path="/project/:projectId/launch" element={<ProjectCapabilityRoute capability="web_onboarding.access"><LaunchActivationPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/launch/legacy" element={<ProjectCapabilityRoute capability="web_onboarding.access"><LaunchReadinessPage /></ProjectCapabilityRoute>} />
          {/* PE-5: /autonomy/v1/* is gated on funnel_insights.access server-side (builderSurface) — the route gate must match or the page renders and then 403s. */}
          <Route path="/project/:projectId/autonomy" element={<ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY}><AutonomyPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/web-placements" element={<ProjectCapabilityRoute capability="web_onboarding.access"><WebPlacementsPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/web-ab-tests" element={<ProjectCapabilityRoute capability="web_onboarding.access"><WebABTestsPage /></ProjectCapabilityRoute>} />
          {/* Stripe Integration routes */}
          <Route path="/project/:projectId/stripe/integration" element={<ProjectCapabilityRoute capability="web_onboarding.access"><StripeIntegrationPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/stripe/products" element={<ProjectCapabilityRoute capability="web_onboarding.access"><PaywallProductsPage /></ProjectCapabilityRoute>} />
          {/* Analytics Settings route */}
          <Route path="/project/:projectId/analytics" element={<ProjectCapabilityRoute capability="web_onboarding.access"><AnalyticsSettingsPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/reports/creative-decisions" element={<CreativeDecisionsFeatureRoute />} />
          {/* People-attribution S10 (D-M): «Traffic into this funnel» as its own Reports page — same access as Reports. */}
          <Route path="/project/:projectId/reports/funnel/:onboardingId/:versionId/sources" element={<ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY}><FunnelTrafficSourcesPage creativeDecisionsEnabled={CREATIVE_DECISIONS_UI_ENABLED} /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/reports" element={<ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY}><FunnelReportsPage creativeDecisionsEnabled={CREATIVE_DECISIONS_UI_ENABLED} /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/experiments" element={<ProjectCapabilityRoute capability={FUNNEL_INSIGHTS_ACCESS_CAPABILITY}><ExperimentsHubPage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/billing" element={<ProjectBillingPage />} />
          {/* Unified Integrations page — no route-level capability gate: each section opens with its own
              subscription inside the page (getIntegrationSectionAccess), and a direct link with no open
              section renders the empty state (sidebar plan 2026-10-07, D-9 / D-11). */}
          <Route path="/project/:projectId/integrations" element={<IntegrationsPage />} />
          {/* Note: legacy /project/:projectId/onboarding/:onboardingId redirects to the V2 list */}
          <Route path="/project/:projectId/permissions" element={<UsersPermissionsPage />} />
          {/* Admin-only Settings route */}
          {isAdmin && <Route path="/settings" element={<SettingsPage />} />}
          {/* Admin-only Guides route */}
          {isAdmin && <Route path="/guides" element={<GuidesPage />} />}
          {/* Admin-only Users route */}
          {isAdmin && <Route path="/users" element={<UsersPage />} />}
          {/* Admin-only Strategies Library route */}
          {isAdmin && <Route path="/strategies-library" element={<StrategiesLibraryPage />} />}
          {/* Admin-only Themes routes */}
          {isAdmin && <Route path="/themes" element={<LegacyThemesRedirect />} />}
          {isAdmin && <Route path="/themes/:themeId" element={<LegacyThemeEditorRedirect />} />}
          {/* Admin-only Themes V2 routes */}
          {isAdmin && <Route path="/themes-v2" element={<ThemesV2Page />} />}
          {isAdmin && <Route path="/themes-v2/:themeId" element={<ThemeV2EditorPage />} />}
          {/* Admin-only Usage Analytics routes */}
          {isAdmin && <Route path="/usage" element={<UsagePage />} />}
          {isAdmin && <Route path="/usage/:projectId" element={<ProjectUsageDetails />} />}
          {/* Admin-only Cron-tasks admin dashboard (Sprint 20) */}
          {isAdmin && <Route path="/admin/cron-tasks" element={<CronTasksAdminPage />} />}
          {isAdmin && <Route path="/admin/provider-credentials" element={<ProviderCredentialsAdminPage />} />}
          {/* Billing catalog admin page gates access internally via
              AuthContext.adminLoaded, so cold-load deep links do not race the
              Firestore admin snapshot and fall through to /all-projects. */}
          <Route path="/admin/billing-catalog" element={<BillingCatalogAdminPage />} />
          {/* Admin-only System Post Templates catalogue (Wave 2 — content-plan) */}
          {isAdmin && <Route path="/admin/system-templates" element={<AdminSystemTemplatesPage />} />}
          {/* Admin-only System Platforms catalogue (content-plan source of truth).
              Route is always registered so cold-load deep links do not race
              AuthContext's Firestore admin snapshot; the page gates access. */}
          <Route path="/admin/system-platforms" element={<AdminSystemPlatformsPage />} />
          <Route path="/admin/ad-platforms" element={<AdminAdCreativePlatformsPage />} />
          {/* B4: the component waits for adminLoaded before enforcing access, so
              cold-load deep links cannot race the Firestore admin snapshot. */}
          <Route path="/admin/prompt-sections" element={<AdminPromptSectionsPage />} />
          {/* Content Plan routes.
              NOTE: NO outer `{isAdmin && ...}` gate here. Admin gating happens
              inside the route components via `adminLoaded` from AuthContext, so
              cold-load deep links do not race the Firestore listener and fall
              through to /all-projects before admin status is known. The route
              components still redirect confirmed non-admins defensively. */}
          <Route path="/project/:projectId/content-plan" element={<ProjectCapabilityRoute capability="content_plan.access"><ContentPlanRoutePage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/content-plan/platform/:platformId" element={<ProjectCapabilityRoute capability="content_plan.access"><ContentPlanPlatformRoutePage /></ProjectCapabilityRoute>} />
          <Route path="/project/:projectId/content-plan/topic/:topicId" element={<ProjectCapabilityRoute capability="content_plan.access"><TopicDetailRoutePage /></ProjectCapabilityRoute>} />
          {/* workflow-golden-tower — admin-gated draft strategy review screen.
              Page-component performs its own admin gate via AuthContext; route
              stays unconditional so cold-load deep links don't race the listener. */}
          <Route path="/project/:projectId/content-plan/strategy/:strategyId/review" element={<ProjectCapabilityRoute capability="content_plan.access"><StrategyDraftReviewPage /></ProjectCapabilityRoute>} />
          {/* <Route path="/task-manager" element={<TaskManagerPage />} /> */} {/* Add TaskManagerPage route */}
          <Route
            path="/"
            element={currentUser ? <Navigate to="/all-projects" replace /> : <Navigate to="/login" replace />}
          />
        </Route>
        <Route
          path="*"
          element={currentUser ? <Navigate to="/all-projects" replace /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <NotificationProvider>
      <CustomThemeProvider> {/* Corrected JSX tag */}
        <IntercomProvider
          appId={INTERCOM_APP_ID}
          apiBase="https://api-iam.intercom.io"
          shouldInitialize={!!INTERCOM_APP_ID}
          onShow={() => { if (import.meta.env.DEV) console.log('[Intercom] Messenger shown'); }}
          onHide={() => { if (import.meta.env.DEV) console.log('[Intercom] Messenger hidden'); }}
        >
          <AuthProvider>
            <IntercomBooter /> {/* Boot/shutdown Intercom on auth state changes */}
            <ProjectModalProvider> {/* Wrap with ProjectModalProvider */}
              <TaskProvider> {/* Wrap with TaskProvider */}
                <UsersPermissionsProvider> {/* Wrap with UsersPermissionsProvider */}
                  <FooterProvider>
                    <ConsoleLogger /> {/* Add ConsoleLogger to log "Hello World" on app mount */}
                    <GuideServiceInitializer /> {/* Initialize guide service integration */}
                    <CssBaseline />
                    <GlobalStyles styles={{ body: { margin: 0, padding: 0, boxSizing: 'border-box' } }} />
                    {/* Intercom z-index override — must render above canvas Portal overlays (z-index: 1300) */}
                    <GlobalStyles styles={{ '#intercom-container, .intercom-lightweight-app, .intercom-namespace': { zIndex: '1400 !important' } }} />
                    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                      <NavigationProvider>
                        <ProjectProvider>
                          <BillingInterruptionProvider>
                            <AppContent />
                          </BillingInterruptionProvider>
                        </ProjectProvider>
                      </NavigationProvider>
                    </Router>
                  </FooterProvider>
                </UsersPermissionsProvider>
              </TaskProvider>
            </ProjectModalProvider>
          </AuthProvider>
        </IntercomProvider>
      </CustomThemeProvider>
    </NotificationProvider>
  );
}

export default App;
